<template>
  <div class="clinical-exam-detail">
    <!-- Back button and actions bar -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <router-link to="/clinical-histories" class="btn btn-outline-primary">
        <i class="bi bi-arrow-left me-2"></i>Volver
      </router-link>

      <div>
        <router-link v-if="patient" :to="`/patients/${patient.id}`" class="btn btn-outline-primary me-2">
          <i class="bi bi-person me-1"></i> Perfil del Paciente
        </router-link>
        <router-link :to="`/clinical-exam/${clinicalExamId}/edit`" class="btn btn-primary">
          <i class="bi bi-pencil me-1"></i> Editar
        </router-link>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ error }}
      <div class="mt-3">
        <router-link to="/clinical-histories" class="btn btn-outline-primary">
          <i class="bi bi-arrow-left me-2"></i>Volver a la lista de historias clínicas
        </router-link>
      </div>
    </div>

    <!-- Exam details -->
    <div v-else-if="clinicalExam && patient" class="row">
      <div class="col-md-8">
        <div class="card mb-4">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Examen Clínico Detallado</h5>
            <span class="text-muted small" v-if="lastUpdated">
              Actualizado: {{ lastUpdated }}
            </span>
          </div>
          <div class="card-body">
            <h2 class="mb-4 border-bottom pb-3">{{ patient.first_name }} {{ patient.last_name }}</h2>

            <!-- Detailed Exam Information -->
            <div class="row g-4">
              <div class="col-md-6">
                <h5 class="text-primary mb-3">Perfil y Análisis</h5>
                <div class="bg-light p-3 rounded">
                  <div class="mb-2">
                    <strong>Perfil:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(formatProfileValue(clinicalExam.profile) || 'No especificado')" :key="'profile-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                  <div class="mb-2">
                    <strong>Cierre Labial:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(formatLipClosureValue(clinicalExam.lip_closure) || 'No especificado')" :key="'lipclosure-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                  <div class="mb-2">
                    <strong>Análisis Frontal:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(formatFrontalAnalysisValue(clinicalExam.frontal_analysis) || 'No especificado')" :key="'frontal-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                  <div>
                    <strong>Altura de Sonrisa:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(formatSmileHeightValue(clinicalExam.smile_height) || 'No especificado')" :key="'smile-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <h5 class="text-primary mb-3">Líneas Medias</h5>
                <div class="bg-light p-3 rounded">
                  <div class="mb-2">
                    <strong>Línea Media Superior:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(formatMidlineValue(clinicalExam.upper_midline) || 'No especificado')" :key="'uppermid-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                  <div>
                    <strong>Línea Media Inferior:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(formatMidlineValue(clinicalExam.lower_midline) || 'No especificado')" :key="'lowermid-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <h5 class="text-primary mb-3">Clasificaciones Dentales</h5>
                <div class="bg-light p-3 rounded">
                  <div class="mb-2">
                    <strong>Clase Canina Derecha:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(clinicalExam.right_canine_class || 'No especificado')" :key="'rightcanine-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                  <div class="mb-2">
                    <strong>Clase Molar Derecha:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(clinicalExam.right_molar_class || 'No especificado')" :key="'rightmolar-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                  <div class="mb-2">
                    <strong>Clase Canina Izquierda:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(clinicalExam.left_canine_class || 'No especificado')" :key="'leftcanine-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                  <div>
                    <strong>Clase Molar Izquierda:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(clinicalExam.left_molar_class || 'No especificado')" :key="'leftmolar-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <h5 class="text-primary mb-3">Medidas y Mordidas</h5>
                <div class="bg-light p-3 rounded">
                  <div class="mb-2">
                    <strong>Canteo Maxilar (mm):</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(clinicalExam.maxillary_cant || 'No especificado')" :key="'maxcant-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                  <div class="mb-2">
                    <strong>Overjet (mm):</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(clinicalExam.overjet || 'No especificado')" :key="'overjet-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                  <div>
                    <strong>Overbite (mm):</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(clinicalExam.overbite || 'No especificado')" :key="'overbite-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <h5 class="text-primary mb-3">Mordidas</h5>
                <div class="bg-light p-3 rounded">
                  <div class="mb-2">
                    <strong>Mordida Cruzada Anterior:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(clinicalExam.anterior_crossbite === 'si' ? 'Sí' : 'No')" :key="'antcross-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                  <div class="mb-2">
                    <strong>Mordida Cruzada Posterior:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(formatCrossbiteLocationValue(clinicalExam.posterior_crossbite))" :key="'postcross-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                  <div class="mb-2">
                    <strong>Mordida Abierta Anterior:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(clinicalExam.anterior_open_bite === 'si' ? 'Sí' : 'No')" :key="'antopen-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                  <div class="mb-2">
                    <strong>Mordida Abierta Posterior:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(formatCrossbiteLocationValue(clinicalExam.posterior_open_bite))" :key="'postopen-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                  <div>
                    <strong>Mordida Profunda:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(clinicalExam.deep_bite === 'si' ? 'Sí' : 'No')" :key="'deepbite-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-12">
                <h5 class="text-primary mb-3">Observaciones Adicionales</h5>
                <div class="bg-light p-3 rounded">
                  <div class="mb-2">
                    <strong>Interposición:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(formatInterpositionValue(clinicalExam.interposition) || 'No especificado')" :key="'interpos-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                  <div>
                    <strong>Observaciones ATM:</strong>
                    <div class="field-content">
                      <p v-for="(line, index) in getTextLines(clinicalExam.tmj_observations || 'Sin observaciones')" :key="'tmj-'+index">
                        {{ line }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <!-- Patient information card (similar to ClinicalHistoryDetail) -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0">Información del Paciente</h5>
          </div>
          <div class="card-body">
            <div class="d-flex align-items-center mb-3">
              <div class="avatar-placeholder me-3">
                {{ getInitials(patient.first_name, patient.last_name) }}
              </div>
              <div>
                <h5 class="mb-0">{{ patient.first_name }} {{ patient.last_name }}</h5>
                <p v-if="patient.fecha_nacimiento" class="text-muted mb-0">
                  {{ formatAge(patient.fecha_nacimiento) }} años
                </p>
              </div>
            </div>

            <!-- Patient details similar to ClinicalHistoryDetail -->
            <div class="patient-info">
              <div class="info-item" v-if="patient.telefono || patient.phone_number">
                <div class="info-label">
                  <i class="bi bi-telephone me-2"></i> Teléfono
                </div>
                <div class="info-value">
                  <p v-for="(line, index) in getTextLines(patient.telefono || patient.phone_number)" :key="'phone-'+index">
                    {{ line }}
                  </p>
                </div>
              </div>
              <div class="info-item" v-if="patient.fecha_nacimiento">
                <div class="info-label">
                  <i class="bi bi-calendar me-2"></i> Fecha de nacimiento
                </div>
                <div class="info-value">
                  <p v-for="(line, index) in getTextLines(formatDate(patient.fecha_nacimiento))" :key="'birth-'+index">
                    {{ line }}
                  </p>
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">
                  <i class="bi bi-clipboard2-check me-2"></i> Estado del Tratamiento
                </div>
                <div class="info-value">
                  <span class="badge" :class="getTreatmentStatusClass(patient.treatment_status)">
                    {{ formatTreatmentStatusValue(patient.treatment_status) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Link to Clinical History -->
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Historia Clínica</h5>
          </div>
          <div class="card-body">
            <router-link 
              v-if="clinicalHistory" 
              :to="`/clinical-histories/${clinicalHistory.id}`" 
              class="btn btn-outline-primary w-100"
            >
              <i class="bi bi-file-medical me-2"></i>Ver Historia Clínica
            </router-link>
            <p v-else class="text-muted text-center">
              No hay historia clínica asociada
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  clinicalExamService, 
  clinicalHistoryService, 
  patientService 
} from '@/services/api';

import { formatDateForDisplay, calculateAge, formatTime, formatDateForInput } from '@/utils/dateUtils';


export default {
  name: 'ClinicalExamDetail',
  data() {
    return {
      clinicalExamId: null,
      clinicalExam: null,
      clinicalHistory: null,
      patient: null,
      loading: true,
      error: null,
      lastUpdated: null
    };
  },
  created() {
    this.clinicalExamId = this.$route.params.id;
    this.fetchData();
  },
  methods: {
    // Split text into lines for line-by-line display
    getTextLines(text) {
      if (!text) return ['No especificado'];
      
      // Split by newlines or create an array with a single item if no newlines
      return String(text).split(/\r?\n/).filter(line => line !== '');
    },
    
    async fetchData() {
      try {
        this.loading = true;

        // Fetch clinical exam data
        const examResponse = await clinicalExamService.get(this.clinicalExamId);
        this.clinicalExam = examResponse.data;

        // Fetch clinical history
        if (this.clinicalExam.clinical_history) {
          const historyResponse = await clinicalHistoryService.get(this.clinicalExam.clinical_history);
          this.clinicalHistory = historyResponse.data;

          // Fetch patient data
          const patientResponse = await patientService.get(this.clinicalHistory.patient);
          this.patient = patientResponse.data;
        }

        // Set last updated
        if (this.clinicalExam.updated_at) {
          this.lastUpdated = this.formatDate(this.clinicalExam.updated_at);
        }
      } catch (error) {
        console.error('Error loading clinical exam details:', error);
        this.error = 'No se pudo cargar el examen clínico. Por favor, intenta de nuevo.';
      } finally {
        this.loading = false;
      }
    },
    getInitials(firstName, lastName) {
      const first = firstName ? firstName.charAt(0).toUpperCase() : '';
      const last = lastName ? lastName.charAt(0).toUpperCase() : '';
      return `${first}${last}`;
    },
    formatDate(dateString) {
      return formatDateForDisplay(dateString);
    },
    formatAge(dateString) {
      return calculateAge(dateString);
    },
    // Formatting functions for clinical exam values
    formatProfileValue(value) {
      const profiles = {
        'concavo': 'Cóncavo',
        'convexo': 'Convexo',
        'recto': 'Recto'
      };
      return profiles[value] || value;
    },
    
    formatLipClosureValue(value) {
      const lipClosures = {
        'normal': 'Normal',
        'forzado': 'Forzado',
        'incompetencia': 'Incompetencia Labial'
      };
      return lipClosures[value] || value;
    },
    
    formatFrontalAnalysisValue(value) {
      const frontalAnalysis = {
        'simetrico': 'Simétrico',
        'asimetrico_derecho': 'Asimétrico Lado Derecho',
        'asimetrico_izquierdo': 'Asimétrico Lado Izquierdo'
      };
      return frontalAnalysis[value] || value;
    },
    
    formatSmileHeightValue(value) {
      const smileHeights = {
        'baja': 'Baja',
        'media': 'Media',
        'alta': 'Alta'
      };
      return smileHeights[value] || value;
    },
    
    formatMidlineValue(value) {
      const midlines = {
        'coincidente': 'Coincidente con Línea Media Facial',
        'desviada_derecha': 'Desviada a la Derecha',
        'desviada_izquierda': 'Desviada a la Izquierda'
      };
      return midlines[value] || value;
    },
    
    formatInterpositionValue(value) {
      const interpositions = {
        'no_presenta': 'No Presenta',
        'labial': 'Labial',
        'lingual': 'Lingual'
      };
      return interpositions[value] || value;
    },
    
    formatCrossbiteLocationValue(value) {
      const locations = {
        'no': 'No',
        'derecha': 'Derecha',
        'izquierda': 'Izquierda'
      };
      return locations[value] || value || 'No';
    },
    
    formatTreatmentStatusValue(value) {
      const statuses = {
        'en_planificacion': 'En Planificación',
        'iniciado': 'Iniciado',
        'completado': 'Terminado',
        'en_pausa': 'En Pausa'
      };
      return statuses[value] || value || 'En Planificación';
    },
    
    getTreatmentStatusClass(status) {
      const statusClasses = {
        'en_planificacion': 'bg-info',      // Blue for planning
        'iniciado': 'bg-warning',           // Yellow for in progress
        'completado': 'bg-success',         // Green for completed
        'en_pausa': 'bg-danger'             // Red for paused
      };

      return statusClasses[status] || 'bg-info';
    }
  }
};
</script>

<style scoped>
/* You can use the same styles from ClinicalHistoryDetail or customize as needed */
.avatar-placeholder {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: bold;
  color: #495057;
}

.patient-info {
  margin-top: 1rem;
}

.info-item {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.info-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.info-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

/* Styles for line-by-line fields */
.field-content p {
  margin-bottom: 0.25rem;
  line-height: 1.5;
}

.field-content p:last-child {
  margin-bottom: 0;
}
</style>